import { create } from 'zustand';
import { 
  connectAndSignWallet, 
  autoConnectWallet, 
  clearWalletCache,
  checkIfWalletIsInstalled,
  switchToBNBChain,
  formatAddress,
  setupWalletListeners
} from '../utils/walletUtils';
import { ethers } from 'ethers';
import { showToast } from '../utils/toast';
import { supabase } from '../lib/supabase';

const CONTRACT_ADDRESS = "0x75a05f185E2EdA5e4DD9E1c878D598490Ca9A6Dc";
const CONTRACT_ABI = [
    "function transactionFee() public view returns (uint256)",
    "function mint(address referrer) external payable",
    "function balanceOf(address owner) public view returns (uint256)"
];

const getRefFromURL = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const ref = urlParams.get('ref');
  return ref ? ref.toLowerCase() : null;
};

const useStore = create((set, get) => ({
  address: null,
  isConnecting: false,
  hasMetaMask: false,
  error: null,

  isMinting: false,
  transactionHash: null,
  userStatus: {
    is_checked_in: false,
    referrer_address: null,
    referral_count: 0,
    nft_rewards: 0,
    phase_1_size: null
  },

  isWalletMenuOpen: false,

  copied: false,
  isGuideOpen: false,

  totalTeam: 0,
  teamLevels: [],

  isTeamUser: false,

  isUploading: false,

  initWallet: () => {
    const hasMetaMask = checkIfWalletIsInstalled();
    set({ hasMetaMask });
  },

  connectWallet: async (onNFTDetected) => {
    try {
      set({ isConnecting: true, error: null });
      
      if (!get().hasMetaMask) {
        throw new Error('Please install MetaMask');
      }

      const address = await connectAndSignWallet(onNFTDetected);
      set({ address, isConnecting: false });
      
      await get().checkUserStatus();
      await get().checkTeamUserStatus();
      await get().ensureUserRegistered();
      
      return address;
    } catch (error) {
      set({ error: error.message, isConnecting: false });
      throw error;
    }
  },

  autoConnect: async (onNFTDetected) => {
    try {
      set({ isConnecting: true, error: null });
      const address = await autoConnectWallet(onNFTDetected);
      
      if (address) {
        const chainSwitched = await switchToBNBChain();
        if (!chainSwitched) {
          throw new Error('Switch to BNB Chain failed');
        }
        
        set({ address, isConnecting: false });
        await get().checkUserStatus();
        await get().checkTeamUserStatus();
        await get().ensureUserRegistered();
      } else {
        set({ isConnecting: false });
      }
      
      return address;
    } catch (error) {
      set({ error: error.message, isConnecting: false });
      return null;
    }
  },

  disconnect: () => {
    clearWalletCache();
    set({ 
      address: null, 
      error: null,
      userStatus: {
        is_checked_in: false,
        referrer_address: null,
        referral_count: 0,
        nft_rewards: 0,
        phase_1_size: null
      }
    });
  },

  mintNFT: async (referrerAddress = null) => {
    try {
      set({ isMinting: true, error: null });
      
      const urlRef = getRefFromURL();
      const finalReferrerAddress = referrerAddress || urlRef || ethers.constants.AddressZero;
      
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const contract = new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, signer);

      const fee = await contract.transactionFee();
      
      const tx = await contract.mint(
        finalReferrerAddress,
        { 
          value: fee,
          gasLimit: 300000 
        }
      );
      
      set({ transactionHash: tx.hash });
      
      const receipt = await tx.wait();
      
      await get().checkUserStatus();
      
      if (receipt.status === 1) {
        await get().registerUser(get().address, finalReferrerAddress);
      }
      
      set({ isMinting: false });
      return { success: receipt.status === 1 };
    } catch (error) {
      set({ 
        error: error.message, 
        isMinting: false 
      });
      throw error;
    }
  },

  checkUserStatus: async () => {
    try {
      const address = get().address;
      if (!address) return;

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const contract = new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, provider);
      const balance = await contract.balanceOf(address);

      const { data, error } = await supabase
        .from('users')
        .select('*')
        .filter('address', 'eq', address.toLowerCase());

      if (error) throw error;

      const userData = data?.[0] || null;
      
      const userStatus = {
        is_checked_in: balance.gt(0),
        referrer_address: userData?.referrer_address || null,
        referral_count: userData?.direct_referral_count || 0,
        indirect_referral_count: userData?.indirect_referral_count || 0,  
        nft_rewards: Math.floor((userData?.direct_referral_count || 0) / 4),
        screenshot_url: userData?.screenshot_url || null,
        color: userData?.color ?? null,
        phase_1_size: userData?.phase_1_size ?? null
      };

      set({ userStatus });
      await get().fetchTeamUserPhase1Size();
      return userStatus;
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),

  formatAddress: (address) => formatAddress(address),

  toggleWalletMenu: () => set(state => ({ 
    isWalletMenuOpen: !state.isWalletMenuOpen 
  })),

  closeWalletMenu: () => set({ 
    isWalletMenuOpen: false 
  }),

  connectWithChainSwitch: async (onNFTDetected) => {
    try {
      set({ isConnecting: true, error: null });
      
      if (!get().hasMetaMask) {
        throw new Error('Please install MetaMask');
      }

      const chainSwitched = await switchToBNBChain();
      if (!chainSwitched) {
        throw new Error('Switch to BNB Chain failed');
      }

      const address = await connectAndSignWallet(onNFTDetected);
      set({ address, isConnecting: false });
      
      await get().checkUserStatus();
      await get().ensureUserRegistered();
      
      return address;
    } catch (error) {
      set({ error: error.message, isConnecting: false });
      throw error;
    }
  },

  initWalletListeners: (navigate) => {
    setupWalletListeners(
      (newAccount) => set({ address: newAccount }),
      () => {
        set({ 
          address: null,
          userStatus: {
            is_checked_in: false,
            referrer_address: null,
            referral_count: 0,
            nft_rewards: 0,
            phase_1_size: null
          }
        });
      }
    );
  },

  setGuideOpen: (isOpen) => set({ isGuideOpen: isOpen }),

  getReferralLink: () => {
    const { address } = get();
    return address ? `${window.location.origin}?ref=${address}` : '';
  },

  copyReferralLink: async () => {
    try {
      const { address } = get();
      const referralLink = `${window.location.origin}?ref=${address}`;
      await navigator.clipboard.writeText(referralLink);
      set({ copied: true });
      showToast.success('Referral link copied to clipboard!');
      setTimeout(() => set({ copied: false }), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
      showToast.error('Failed to copy referral link');
    }
  },

  fetchTeamStats: async () => {
    try {
      const { address } = get();
      if (!address) return;

      const { data: teamStats, error } = await supabase
        .from('referral_relationships')
        .select('level, count(*)')
        .eq('ancestor_address', address)
        .group('level')
        .order('level');

      if (error) throw error;

      const totalTeam = teamStats.reduce((sum, { count }) => sum + count, 0);
      
      set({ 
        totalTeam,
        teamLevels: teamStats 
      });
      
    } catch (error) {
      set({ error: error.message });
    }
  },

  registerUser: async (userAddress, referrerAddress) => {
    try {
      const normalizedUserAddress = userAddress?.toLowerCase();
      const normalizedReferrerAddress = referrerAddress?.toLowerCase();
      
      if (!normalizedUserAddress) {
        return;
      }

      const finalReferrerAddress = (
        !normalizedReferrerAddress || 
        normalizedReferrerAddress === normalizedUserAddress ||
        normalizedReferrerAddress === ethers.constants.AddressZero.toLowerCase()
      ) ? null : normalizedReferrerAddress;

      const color = Math.floor(Math.random() * 4) + 1;

      const { error } = await supabase
        .from('users')
        .insert([
          { 
            address: normalizedUserAddress,
            referrer_address: finalReferrerAddress,
            direct_referral_count: 0,
            indirect_referral_count: 0,
            color
          }
        ]);

      if (error) {
        if (error.code === '23505' && finalReferrerAddress) {
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('referrer_address')
            .eq('address', normalizedUserAddress)
            .single();
          
          if (!userError && (!userData.referrer_address)) {
            await supabase
              .from('users')
              .update({ referrer_address: finalReferrerAddress })
              .eq('address', normalizedUserAddress);
          }
        }
      }
      
      await get().fetchTeamStats();
      
    } catch (error) {
      console.error("Registration process failed:", error);
    }
  },

  checkTeamUserStatus: async () => {
    try {
      const { address } = get();
      if (!address) return false;
      
      const normalizedAddress = address.toLowerCase();
      
      const { data, error } = await supabase
        .from('team_users')
        .select('address')
        .ilike('address', normalizedAddress);
        
      if (error) {
        set({ isTeamUser: false });
        return false;
      }
      
      const isTeam = data && data.length > 0;
      set({ isTeamUser: isTeam });
      return isTeam;
    } catch (error) {
      set({ error: error.message, isTeamUser: false });
      return false;
    }
  },

  uploadScreenshot: async (file) => {
    const { address } = get();
    if (!address) return;
    
    try {
      set({ isUploading: true });
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${address.toLowerCase()}_${Date.now()}.${fileExt}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('screenshot')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });
      
      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }
      
      const { data: urlData } = supabase.storage
        .from('screenshot')
        .getPublicUrl(fileName);
      
      const publicUrl = urlData.publicUrl;
      
      const { error: updateError } = await supabase
        .from('users')
        .update({ screenshot_url: publicUrl })
        .match({ address: address.toLowerCase() });
      
      if (updateError) {
        console.error('Update error:', updateError);
        throw updateError;
      }
      
      showToast.success('Screenshot uploaded successfully!');
      
      await get().checkUserStatus();
      
      return publicUrl;
    } catch (error) {
      console.error('Screenshot upload failed:', error);
      showToast.error('Failed to upload screenshot');
      throw error;
    } finally {
      set({ isUploading: false });
    }
  },

  ensureUserRegistered: async () => {
    try {
      const address = get().address;
      if (!address) return;

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const contract = new ethers.Contract(CONTRACT_ADDRESS, CONTRACT_ABI, provider);
      const balance = await contract.balanceOf(address);

      if (balance.gt(0)) {
        const { data, error } = await supabase
          .from('users')
          .select('address')
          .filter('address', 'eq', address.toLowerCase());

        if (error) throw error;

        if (!data || data.length === 0) {
          const urlRef = getRefFromURL();
          await get().registerUser(address, urlRef || ethers.constants.AddressZero);
        }
      }
    } catch (error) {
    }
  },

  fetchTeamUserPhase1Size: async () => {
    try {
      const address = get().address;
      if (!address) return;

      const { data, error } = await supabase
        .from('team_users')
        .select('phase_1_size')
        .eq('address', address.toLowerCase())
        .single();

      if (error) throw error;

      set(state => ({
        userStatus: {
          ...state.userStatus,
          phase_1_size: data?.phase_1_size ?? null
        }
      }));
      return data?.phase_1_size ?? null;
    } catch (error) {
      set({ error: error.message });
      return null;
    }
  }
}));

export default useStore;